/**
 * RBPE API client — talks to the real risk-pricing-engine FastAPI service,
 * proxied through platform-api's gateway at `/risk-pricing-engine/...`.
 *
 * Follows the same convention as UnderwritingEngineV2/lib/controllerApi.ts:
 * axios directly, a Keycloak bearer token attached via UserService, and the
 * backend's `{status, data}` envelope unwrapped here so callers just get `T`.
 */
import axios from "axios";
// @ts-ignore - JS module, no types
import UserService from "../../../../services/UserService";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const serverConfig = require("../../../../config/server.js");

const BASE = `${serverConfig.api_base_url}risk-pricing-engine`;

interface ApiEnvelope<T> {
  status: string;
  data: T;
}

async function authConfig() {
  const token = await UserService.ensureFreshToken();
  return { headers: { Authorization: `Bearer ${token}` } };
}

/** Drops undefined/empty-string filter values so "All divisions"-style
 * selections don't get sent as a literal empty query param. */
function cleanParams(
  params?: Record<string, string | undefined>,
): Record<string, string> | undefined {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string] => {
      const value = entry[1];
      return value != null && value !== "";
    }),
  );
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | undefined>,
): Promise<T> {
  const config = await authConfig();
  const response = await axios.get<ApiEnvelope<T>>(`${BASE}${path}`, {
    ...config,
    params: cleanParams(params),
  });
  return response.data.data;
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  params?: Record<string, string | undefined>,
): Promise<T> {
  const config = await authConfig();
  const response = await axios.post<ApiEnvelope<T>>(`${BASE}${path}`, body, {
    ...config,
    params: cleanParams(params),
  });
  return response.data.data;
}

/** For file downloads (e.g. CSV export) -- the gateway requires the same
 * bearer token apiGet/apiPost attach, so a plain `<a href>` fails; this
 * fetches the raw file (not the `{status, data}` envelope) as a blob. */
export async function apiGetBlob(
  path: string,
  params?: Record<string, string | undefined>,
): Promise<Blob> {
  const config = await authConfig();
  const response = await axios.get(`${BASE}${path}`, {
    ...config,
    params: cleanParams(params),
    responseType: "blob",
  });
  return response.data;
}

export const MOCK_LATENCY_MS = 320;

/**
 * Retained for the handful of not-yet-backed write actions (e.g. Calibration's
 * K promotion) and the per-module deterministic data generators that are now
 * dead code but kept around rather than deleted destructively.
 */
export function mockResponse<T>(value: T, latency = MOCK_LATENCY_MS): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), latency);
  });
}

/** Deterministic pseudo-random generator so mock values never flicker. */
export function seeded(seed: number) {
  let state = seed % 2147483647;
  if (state <= 0) state += 2147483646;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
}

export function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}
